import React, { useContext, useState, useEffect } from "react";
import Slider from "react-slick";
import Icon from "../Icon/icon";
import ResponsiveWrapper from "../_responsive-wrapper";
import { PatioContext } from "@root/context/patio-calculator";
import addImageDimensions from "@root/utils/addImageDimensions";

const ArrowButton = ({ direction, onClick }) => (
    <button
        type="button"
        aria-label={direction === "left" ? "Previous" : "Next"}
        onClick={onClick}
        className="image-gallery__btn-wrapper"
    >
        <Icon icon={direction === "left" ? "chevron-left" : "chevron-right"} className="image-gallery__btn" color="currentColor" />
    </button>
);

const CenterArrowButton = ({ direction, onClick, disabled }) => (
    <button
        type="button"
        aria-label={direction === "left" ? "Previous image" : "Next image"}
        onClick={onClick}
        disabled={disabled}
        className={[
            "image-gallery__center-arrow",
            direction === "left" ? "image-gallery__center-arrow--left" : "image-gallery__center-arrow--right",
        ].join(" ")}
    >
        <Icon icon={direction === "left" ? "chevron-left" : "chevron-right"} className="image-gallery__center-arrow-icon" color="currentColor" />
    </button>
);

const getDotSizeClass = (index, activeSlide) => {
    const distance = Math.abs(index - activeSlide);

    if (distance === 0) return "image-gallery__dot--active";
    if (distance === 1) return "image-gallery__dot--normal-size";
    if (distance === 2) return "image-gallery__dot--smaller-size";
    if (distance === 3) return "image-gallery__dot--smallest-size";
    if (distance >= 4) return "image-gallery__dot--hidden";

    return "";
};

const appendDots = (dots, activeSlide) => {
    const translateX = -((activeSlide + 0.5) * 20);
    return (
        <div>
            <div className="image-gallery__dots-container">
                <ul className="image-gallery__dots" style={{ transform: `translateX( calc( ${translateX}px + 50%) )` }}>
                    {dots.map((dot, index) => {
                        return React.cloneElement(dot, { className: `image-gallery__dot ${getDotSizeClass(index, activeSlide)}` });
                    })}
                </ul>
            </div>
        </div>
    );
};

const customPaging = () => (
    <button type="button" className="image-gallery__dot-button" aria-label="dot-button">
        <span className="image-gallery__dot-content" />
    </button>
);

const ImageGallery = ({
    images,
    thumbsSlider,
    slideshowSlider,
    setSlideshowSlider,
    setThumbsSlider,
    slideTo,
    measurements,
    isVariantImage,
    WarrantyBadge,
    showArrows = false,
}) => {
    const { configuration, activeCoordinate, setActiveCoordinate, measurements: measurementsValues } = useContext(PatioContext) || {};
    const [activeSlide, setActiveSlide] = useState(0);

    const slideshowSettings = {
        dots: false,
        arrows: showArrows,
        infinite: true,
        speed: 375,
        slidesToShow: 1,
        slidesToScroll: 1,
        fade: true,
        // lazyLoad: true, We need to add this after some time until most of the images are generated in the CDN
        //adaptiveHeight: true, removed becaouse it was jumping on first load
        prevArrow: showArrows ? <ArrowButton direction="left" /> : null,
        nextArrow: showArrows ? <ArrowButton direction="right" /> : null,
        beforeChange: (current, next) => {
            setActiveSlide(next);
        },
        responsive: [
            {
                breakpoint: 768,
                infinite: false,
                settings: { dots: false },
            },
        ],
        appendDots: (dots) => appendDots(dots, activeSlide),
        customPaging,
    };

    const gallerySettings = {
        dots: false,
        arrows: true,
        speed: 375,
        infinite: true,
        slidesToShow: images?.length <= 5 ? images.length : 5,
        slidesToScroll: 1,
        prevArrow: <ArrowButton direction="left" />,
        nextArrow: <ArrowButton direction="right" />,
    };

    const indexToLetter = (index) => String.fromCharCode(65 + index);

    function imageMarkupWithCoordinates() {
        return (
            <>
                <div className="measurements-widget__coordinates">
                    {measurements?.map((measurement, index) => {
                        let text = indexToLetter(index);
                        const style = { left: `${measurement.x * 100}%`, top: `${measurement.y * 100}%` };

                        const active = activeCoordinate === measurement.key;
                        const defaultValue = measurementsValues[measurement.key]?.default;
                        const filled = !defaultValue;

                        const classes = [
                            `measurements-widget__coordinate`,
                            active && "measurements-widget__coordinate--active",
                            filled && "measurements-widget__coordinate--past",
                            defaultValue && "measurements-widget__coordinate--default",
                        ].filter(Boolean).join(" ");

                        if ((filled || active) && !defaultValue) {
                            text = `${configuration[measurement.key]}''`;
                        }

                        const changeMeasurement = () => {
                            const startButton = document.querySelector('.configurator-section__header button');
                            if (startButton) {
                                startButton.click();
                            }
                            setActiveCoordinate(measurements[index].key);
                        }

                        return (
                            <div
                                key={index}
                                className={classes}
                                style={style}
                                onClick={() => changeMeasurement(index)}
                            >
                                <span>{text}</span>
                            </div>
                        )
                    }
                    )}
                </div>

                <div className={"measurements-widget__image-lines"}>
                    {measurements?.map((measurement, index) => {
                        const linePoints = measurement.line_points;
                        if (!linePoints) return null;

                        const active = activeCoordinate === measurement.key;
                        const defaultValue = measurementsValues[measurement.key]?.default;
                        const filled = !defaultValue;
                        let color = "#800080";
                        let secondColor = "#fff";
                        if (!active) {
                            color = "#4443";
                            secondColor = "#fff3";
                        }

                        const x1 = linePoints.point_one_x * 100;
                        const y1 = linePoints.point_one_y * 100;
                        const x2 = linePoints.point_two_x * 100;
                        const y2 = linePoints.point_two_y * 100;
                        const x3 = linePoints.curve_start_x * 100;
                        const y3 = linePoints.curve_start_y * 100;
                        const x4 = linePoints.curve_end_x * 100;
                        const y4 = linePoints.curve_end_y * 100;


                        // Direction vector
                        const dx = x2 - x1;
                        const dy = y2 - y1;

                        // Normalize perpendicular vector
                        const length = Math.sqrt(dx * dx + dy * dy);
                        const offset = 1.2; // length of the normal line (in % coordinates)
                        const nx = -(dy / length) * offset;
                        const ny = (dx / length) * offset;

                        const classes = [
                            `measurements-widget__image-line`,
                            active && "measurements-widget__image-line--active",
                            filled && "measurements-widget__image-line--past",
                        ].filter(Boolean).join(" ");

                        const isCurved = measurement.line_points?.isCurved;
                        const startX = x1 * 4;
                        const startY = y1 * 4;
                        const endX = x2 * 4;
                        const endY = y2 * 4;
                        const control1X = x3 * 4;
                        const control1Y = y3 * 4;
                        const control2X = x4 * 4;
                        const control2Y = y4 * 4;


                        const pathData = `M ${startX} ${startY} C ${control1X} ${control1Y}, ${control2X} ${control2Y}, ${endX} ${endY}`;

                        return (
                            <>
                                {isCurved && (
                                    <svg viewBox={`0 0 400 400`} key={index} className={classes}>
                                        <path
                                            d={pathData}
                                            stroke={color}
                                            strokeWidth="2"
                                            strokeDasharray="5,5"
                                            // Add fill="none" to prevent the shape from being filled with black
                                            fill="none"
                                            style={{
                                                fill: "none",
                                            }}
                                        />
                                        <path
                                            d={pathData}
                                            stroke={secondColor}
                                            strokeWidth="2"
                                            strokeDasharray="5,5"
                                            strokeDashoffset="5"
                                            fill="none"
                                            style={{
                                                fill: "none",
                                            }}
                                        />
                                    </svg>
                                )}
                                {
                                    !isCurved && (
                                        <svg viewBox={`0 0 400 400`} key={index} className={classes}>
                                            <line
                                                x1={`${x1}%`}
                                                y1={`${y1}%`}
                                                x2={`${x2}%`}
                                                y2={`${y2}%`}
                                                strokeWidth="2"
                                                stroke={color}
                                                strokeDasharray="5,5"

                                            />

                                            <line
                                                x1={`${x1}%`}
                                                y1={`${y1}%`}
                                                x2={`${x2}%`}
                                                y2={`${y2}%`}
                                                strokeWidth="2"
                                                stroke={secondColor}
                                                strokeDasharray="5,5"
                                                strokeDashoffset="5"

                                            />

                                            <line
                                                x1={`${x1 - nx}%`}
                                                y1={`${y1 - ny}%`}
                                                x2={`${x1 + nx}%`}
                                                y2={`${y1 + ny}%`}
                                                stroke={color}
                                                strokeWidth="2"

                                            />

                                            <line
                                                x1={`${x2 - nx}%`}
                                                y1={`${y2 - ny}%`}
                                                x2={`${x2 + nx}%`}
                                                y2={`${y2 + ny}%`}
                                                stroke={color}
                                                strokeWidth="2"

                                            />
                                        </svg>
                                    )
                                }

                            </>

                        );
                    })}
                </div>
            </>
        );
    }

    useEffect(() => {
        if (activeCoordinate) {
            slideTo(0);
        }
    }, [activeCoordinate]);

    const canNavigate = (images?.length ?? 0) > 1;
    const goPrev = () => slideshowSlider?.slickPrev?.();
    const goNext = () => slideshowSlider?.slickNext?.();

    return (
        <ResponsiveWrapper className="image-gallery">
            <div className="image-gallery__main-container">
                <Slider
                    className="image-gallery__main"
                    asNavFor={thumbsSlider}
                    ref={(slider1) => setSlideshowSlider(slider1)}
                    {...slideshowSettings}
                >
                    {images?.map((image, imageIdx) => (
                        <div className="image-gallery__large-image-ratio" key={image?.node?.url}>
                            {imageIdx === 0 && imageMarkupWithCoordinates()}

                            {/**imageIdx === 0 && measurements?.map((measurement, index) => {
                                const { key, x, y } = measurement;

                                if(!x || !y) return null;

                                return (
                                    <div
                                        key={key}
                                        className={`image-gallery__coordinate ${activeCoordinate === key ? "image-gallery__coordinate--active" : ""}`}
                                        style={{"--x": x, "--y": y }}
                                        onClick={() => setActiveCoordinate(key)}
                                    >
                                        <div className="image-gallery__coordinate-dot">{indexToLetter(index)}</div>
                                        <div className="image-gallery__coordinate-tooltip">{configuration[key]}″</div>
                                    </div>
                                );
                            })**/}
                            {WarrantyBadge && isVariantImage(image?.node ?? image) && WarrantyBadge() ? (
                                <WarrantyBadge className="image-gallery-slideshow__item-badge" />
                            ) : null}
                            <img className="image-gallery__large-image" alt={image?.title} src={addImageDimensions(image?.src, 800)} />
                        </div>
                    ))}
                </Slider>

                {canNavigate ? (
                    <>
                        <CenterArrowButton direction="left" onClick={goPrev} disabled={!slideshowSlider} />
                        <CenterArrowButton direction="right" onClick={goNext} disabled={!slideshowSlider} />
                    </>
                ) : null}
            </div>
            <Slider
                className="image-gallery__carousel"
                asNavFor={slideshowSlider}
                ref={(slider2) => setThumbsSlider(slider2)}
                {...gallerySettings}
            >
                {images?.map((image, index) => (
                    <div className="image-gallery__thumbnail">
                        <div className="image-gallery__thumbnail-ratio">
                            <button
                                type="button"
                                onClick={() => slideTo(index)}
                            >
                                <img className="image-gallery__thumbnail-image" alt={image?.title} src={addImageDimensions(image?.src, 200)} />
                            </button>
                        </div>
                    </div>
                ))}
            </Slider>
        </ResponsiveWrapper>
    );
};

export default ImageGallery;
